// 矢印図形の幾何ヘルパー。
// ドラッグ配置した矢印（ShapeCanvasObject.arrowFromCorner が定義済み）の描画座標を
// 算出する。SVG（shape-object.tsx）・Canvas2D（PDF / サムネイル）の各描画経路で共有し、
// 矢印の見た目を一致させる。座標はすべて図形ローカル座標（0..w, 0..h）で返す。

import { clampFrameToCanvas, type ArrowCorner } from "@refinement-canvas/domain";

/** 矢印の描画に必要な座標一式（図形ローカル座標）。 */
export interface ArrowPath {
  /** 矢印の起点（尻側）。 */
  readonly tail: { readonly x: number; readonly y: number };
  /** 矢印の終点（矢じり先端）。 */
  readonly head: { readonly x: number; readonly y: number };
  /** 矢じりの片翼の先端。 */
  readonly wingA: { readonly x: number; readonly y: number };
  /** 矢じりのもう片翼の先端。 */
  readonly wingB: { readonly x: number; readonly y: number };
}

// 矢じりが軸（始点→終点の直線）に対して占めてよい最大の長さ比。極小の矢印で矢じりが
// 軸より長くなって外接矩形外へはみ出すのを防ぐための上限。これを超える場合は矢じりを縮小する。
const ARROW_HEAD_MAX_LENGTH_RATIO = 0.6;

// 各角のローカル座標を返す関数表。
const CORNER_POSITION: Record<ArrowCorner, (w: number, h: number) => { x: number; y: number }> = {
  "top-left": () => ({ x: 0, y: 0 }),
  "top-right": (w) => ({ x: w, y: 0 }),
  "bottom-left": (_w, h) => ({ x: 0, y: h }),
  "bottom-right": (w, h) => ({ x: w, y: h }),
};

// 起点角に対する対角（=矢じり側の角）。
const OPPOSITE_CORNER: Record<ArrowCorner, ArrowCorner> = {
  "top-left": "bottom-right",
  "top-right": "bottom-left",
  "bottom-left": "top-right",
  "bottom-right": "top-left",
};

/**
 * 外接矩形（w×h）と起点角から、矢印の軸と矢じりの座標を求める。
 * @param headLength 矢じりの軸方向の長さ（px）。
 * @param headHalfWidth 矢じりの軸に直交する片側の張り出し量（px）。
 *
 * 極小の矢印（軸長が短いケース）では、矢じりが軸より長くなって根元（base）が尻（tail）を
 * 通り越し、座標が外接矩形の外（負値など）へ大きくはみ出して見た目が破綻する。これを防ぐため、
 * 矢じり長が軸長の {@link ARROW_HEAD_MAX_LENGTH_RATIO} を超える場合は、縦横比を保ったまま
 * 矢じり（長さ・幅）を縮小し、根元を必ず軸（tail→head の線分）上＝外接矩形内に収める。
 * 通常サイズ（軸長が十分長い）では縮小されない。
 *
 * 注意（仕様）: 矢じりの先端は「ドラッグのドロップ点（=外接矩形の角）」に一致させる仕様のため、
 * 16×100 のような極端に細長い対角矢印では、矢じりの翼（wing）が短辺側で外接矩形を数 px だけ
 * はみ出すことがある。先端を角に固定しつつ翼を見せる以上これは幾何学的に不可避で、矢じりの
 * 自然な開きとして許容する。画面 SVG は overflow="visible"、PDF / サムネイルは図形セルを
 * クリップせず描画するため、はみ出した翼も欠けずに表示される。選択枠は「ドラッグ矩形（軸の
 * 両端）」を境界とする意図的な仕様で、矢じりの翼までは含めない。
 */
export function resolveArrowPath(
  w: number,
  h: number,
  fromCorner: ArrowCorner,
  headLength: number,
  headHalfWidth: number
): ArrowPath {
  const tail = CORNER_POSITION[fromCorner](w, h);
  const head = CORNER_POSITION[OPPOSITE_CORNER[fromCorner]](w, h);
  const dx = head.x - tail.x;
  const dy = head.y - tail.y;
  // 0 割りを避けるため最小 1 とする（極小サイズでも破綻しないようにする）。
  const length = Math.hypot(dx, dy) || 1;
  const ux = dx / length;
  const uy = dy / length;
  // 矢じりが軸より長くなる極小ケースでは、縦横比を保って矢じり全体を縮小する。
  const maxHeadLength = length * ARROW_HEAD_MAX_LENGTH_RATIO;
  const scale = headLength > maxHeadLength ? maxHeadLength / headLength : 1;
  const effHeadLength = headLength * scale;
  const effHeadHalf = headHalfWidth * scale;
  // 矢じりの根元（先端から軸方向へ effHeadLength 戻った点）。
  const baseX = head.x - ux * effHeadLength;
  const baseY = head.y - uy * effHeadLength;
  // 軸に直交する単位ベクトル。
  const px = -uy;
  const py = ux;
  return {
    tail,
    head,
    wingA: { x: baseX + px * effHeadHalf, y: baseY + py * effHeadHalf },
    wingB: { x: baseX - px * effHeadHalf, y: baseY - py * effHeadHalf },
  };
}

/**
 * ドラッグの変位（終点 - 起点）から矢印の起点角を求める。
 * 右下方向のドラッグなら起点は左上、というように、ドラッグ開始点が尻側になるよう対応付ける。
 * 0 は「正方向（右 / 下）」として扱う。
 */
export function arrowCornerFromDrag(dx: number, dy: number): ArrowCorner {
  const right = dx >= 0;
  const down = dy >= 0;
  if (right && down) return "top-left";
  if (right && !down) return "bottom-left";
  if (!right && down) return "top-right";
  return "bottom-right";
}

/** ドラッグ配置で確定する図形枠。矢印のときは起点角も保持する。 */
export interface ShapeDragFrame {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
  readonly arrowFromCorner: ArrowCorner;
}

/**
 * ドラッグの始点・終点（ステージ座標）から、図形を配置するための枠を求める。
 * - グリッド吸着 ON のときは始点・終点をそれぞれ snapPx に丸める。
 * - 退化（0 サイズ）を避けるため幅・高さは最低 minSize を確保する。
 * - キャンバス範囲内へ clamp する。
 * 矢印の向き（arrowFromCorner）は丸め前の生のドラッグ方向から決め、吸着でサイズが
 * 0 に潰れても向きが安定するようにする。ドラッグ配置プレビューと確定処理で同じ結果を
 * 得るために両者で共有する。
 *
 * 最小サイズで枠を広げる際は、生のドラッグ方向（startX/startY と endX/endY の符号）に
 * 合わせて「開始点側の角」を固定して反対方向へ広げる。これをしないと、短い負方向ドラッグ
 * （左 / 上方向で minSize が効くケース）やグリッド吸着で端点が同じセルへ丸まるケースで、
 * arrowFromCorner が示す起点角（=ドラッグ開始点）が枠の角からずれてしまう
 * （「開始点が常に対角の一方になる」仕様に反する）。
 *
 * 注意（境界の制約）: 開始点がキャンバス端から minSize 未満の位置にあり、かつその端の方向へ
 * 負方向ドラッグした場合は、開始点側の角を保つと枠がキャンバス外へはみ出すため、最後の
 * clampFrameToCanvas が「キャンバス内維持」を優先して枠を内側へ寄せ、起点角が数 px ずれる。
 * これは「開始点 = 起点角」と「枠はキャンバス内」を同時に満たせない幾何学的な制約で、
 * 後者（キャンバス内維持＝オブジェクトは必ず範囲内）を優先する仕様とする。
 */
export function computeShapeDragFrame(
  startX: number,
  startY: number,
  endX: number,
  endY: number,
  options: {
    readonly gridSnap: boolean;
    readonly snapPx: number;
    readonly minSize: number;
    /** 幅・高さで異なる最小値が必要な種別（セクション等）向けの上書き。未指定は minSize を使う。 */
    readonly minW?: number | undefined;
    readonly minH?: number | undefined;
  }
): ShapeDragFrame {
  const arrowFromCorner = arrowCornerFromDrag(endX - startX, endY - startY);
  const snap = (value: number): number =>
    options.gridSnap ? Math.round(value / options.snapPx) * options.snapPx : value;
  const sx = snap(startX);
  const sy = snap(startY);
  const ex = snap(endX);
  const ey = snap(endY);
  const rawW = Math.abs(ex - sx);
  const rawH = Math.abs(ey - sy);
  const w = Math.max(options.minW ?? options.minSize, rawW);
  const h = Math.max(options.minH ?? options.minSize, rawH);
  // 生のドラッグ方向。左 / 上へ向かう場合は開始点（sx / sy）を枠の右端 / 下端に固定し、
  // 反対方向（左 / 上）へ minSize 分広げることで、開始点を起点角に残す。
  const growsLeft = endX < startX;
  const growsUp = endY < startY;
  const frame = clampFrameToCanvas({
    x: growsLeft ? sx - w : sx,
    y: growsUp ? sy - h : sy,
    w,
    h,
  });
  return { ...frame, arrowFromCorner };
}
